import { apiSlice } from '@/api'
import {
  buildManualCheckPaymentBody,
  buildManualPayPaymentBody
} from '@/utils/manual-payment-request'
import { normalizeRecognitionsReport } from '@/utils/recognitionsReport'

const parseReportJsonResponse = async (response) => {
  const text = await response.text()
  if (!text.trim()) return null

  return JSON.parse(text)
}

const normalizeTransactionsResponse = (response) =>
  response ?? { transactions: [] }

const normalizeAuditResponse = (response) => {
  if (!response) return { rows: [] }
  if (Array.isArray(response)) return { rows: response }
  return { rows: response.rows ?? [] }
}

export const reportApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    checkNumberPayment: builder.mutation({
      query: (params) => {
        return {
          url: 'api/v2/billing/manual/check',
          method: 'POST',
          body: buildManualCheckPaymentBody(params)
        }
      },
      extraOptions: { maxRetries: 0 }
    }),

    postNumberPayment: builder.mutation({
      query: (params) => {
        return {
          url: 'api/v2/billing/manual/pay',
          method: 'POST',
          body: buildManualPayPaymentBody(params)
        }
      },
      extraOptions: { maxRetries: 0 }
    }),

    postPay: builder.mutation({
      query: (arg) => {
        const { body, ofd = false } = arg

        return {
          url: `api/v2/billing/manual/pay?ofd=${ofd}`,
          method: 'POST',
          body: buildManualPayPaymentBody(body)
        }
      }
    }),

    manualCheck: builder.mutation({
      query: ({ id, body }) => ({
        url: `api/v2/billing/manual/ofd/${id}`,
        method: 'POST',
        body,
        responseHandler: 'text'
      }),
      transformResponse: (response) => response
    }),

    getReports: builder.query({
      query: (arg) => {
        const { id, from, to } = arg
        return {
          url: `api/v1/report/${id}`,
          method: 'GET',
          params: { from, to }
        }
      }
    }),

    getTransactions: builder.query({
      query: (arg) => {
        const { id, from, to, search } = arg
        const params = { from, to }
        if (search) params.search = search
        return {
          url: `api/v2/reports/transactions/${id}`,
          method: 'GET',
          params
        }
      },
      responseHandler: parseReportJsonResponse,
      transformResponse: normalizeTransactionsResponse,
      extraOptions: { maxRetries: 0 }
    }),

    getRecognition: builder.query({
      query: (arg) => {
        const { id, from, to } = arg
        return {
          url: `api/v2/reports/recognitions/${id}`,
          method: 'GET',
          params: { from, to }
        }
      },
      responseHandler: parseReportJsonResponse,
      transformResponse: normalizeRecognitionsReport
    }),

    getClientFlow: builder.query({
      query: (arg) => {
        const { id, from, to } = arg
        return {
          url: `api/v2/persons/report/building/${id}/hik`,
          method: 'GET',
          params: { from, to }
        }
      },
      extraOptions: { maxRetries: 0 }
    }),

    getDebtors: builder.query({
      query: (arg) => {
        const { id, page = 1, size = 10 } = arg
        return {
          url: `api/v2/billing/building/${id}/debtors`,
          method: 'GET',
          params: { page, size }
        }
      }
    }),

    getAudit: builder.query({
      query: (arg) => {
        const { buildingId, plateNumber, from, to } = arg
        const params = {}

        if (plateNumber && plateNumber.trim()) {
          params.plate_number = plateNumber.trim()
        }

        if (from) params.from = from
        if (to) params.to = to

        return {
          url: `api/v2/reports/audit/${buildingId}`,
          method: 'GET',
          params
        }
      },
      responseHandler: parseReportJsonResponse,
      transformResponse: normalizeAuditResponse
    }),

    updatePlateNumber: builder.mutation({
      query: ({ id, plateNumber }) => ({
        url: `api/v2/billing/${id}/plate_number`,
        method: 'PATCH',
        body: { plate_number: plateNumber }
      })
    })
  })
})

export const {
  useCheckNumberPaymentMutation,
  usePostNumberPaymentMutation,
  usePostPayMutation,
  useManualCheckMutation,
  useLazyGetReportsQuery,
  useGetReportsQuery,
  useLazyGetTransactionsQuery,
  useLazyGetRecognitionQuery,
  useLazyGetClientFlowQuery,
  useLazyGetDebtorsQuery,
  useLazyGetAuditQuery,
  useUpdatePlateNumberMutation
} = reportApi;
